import { accountIdParam, RedirectType, RegistrationMode } from '@ytl/common-model';
import { Button, Input, makeLink, Select } from '@ytl/common-web';
import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as BuildingIcon } from '../../assets/building.svg';
import { ReactComponent as EnvelopeIcon } from '../../assets/envelope.svg';
import { ReactComponent as ScaleIcon } from '../../assets/scale.svg';
import { ReactComponent as UnionIcon } from '../../assets/union.svg';
import { useApp } from '../../contexts/appContext';
import { useLanguage } from '../../contexts/languageContext';
import { RegistrationPhase } from '../../pages/RegisterPage';
import { AppRoutes } from '../../routes';
import { SetPassword } from '../../shared/components/custom/set-password/SetPassword';
import { getConfig } from '../../shared/data/config';
import { accountResource } from '../../shared/resource/account.resource';
import { mfaResource } from '../../shared/resource/mfa.resource';
import { useLinkParams } from '../../shared/utils/useQueryParams';
import { RegisterYoloOtpWebView } from '../register/RegisterYoloOtpWebView';
import { useRegister } from './service/useRegister';

export type RegisterInAppProps = {
  registrationMode: RegistrationMode;
};

export const RegisterB2bWebView = ({ registrationMode }: RegisterInAppProps) => {
  const { translation } = useLanguage();
  const {
    isLoading,
    setIsLoading,
    registerError,
    view,
    setView,
    email,
    setEmail,
    emailError,
    emailAlreadyUsed,
    finalizeRegistrationB2b,
    validateOtp,
    completeRegistrationB2b,
    _usePassword,
    needsRedirect,
    yoloResend,
    setNeedsRedirect,
    messageWithAction,
    emailJwt,
  } = useRegister(registrationMode);
  const {
    password,
    setPassword,
    passwordAgain,
    setPasswordAgain,
    getPasswordHints,
    passwordsAreNotMatching,
    hasPasswordError,
    reportPasswordErrors,
    passwordErrors,
  } = _usePassword;
  const [otp, setOtp] = useState('');
  const [phoneNumber, setPhoneNumber] = useState<{ value: string; label: string } | null>(null);
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  const prevViewRef = useRef(RegistrationPhase.Nil);

  const handleEdit = useCallback((e: KeyboardEvent) => {
    if (!['Escape', 'ArrowUp', 'ArrowDown', 'Tab', 'ShiftTab', 'Enter', 'NumpadEnter'].includes(e.code)) {
      e.preventDefault();
    }
  }, []);

  const registerButtonEnabled = useMemo(() => {
    return email.length > 0;
  }, [email]);
  const { registerAccountId } = useApp();
  const { pathname, search } = useLocation();

  const [loginLink, setLoginLink] = useState<string>(AppRoutes.homepage);

  const linkParams = useLinkParams();

  const fetchLoginLink = useMemo(
    () => async () => {
      if (emailAlreadyUsed) {
        const loginLink = await getLoginUrl();
        // const link = makeLink(loginLink, linkParams);
        if (loginLink) {
          setLoginLink(loginLink);
        }
      }
    },
    [emailAlreadyUsed, setLoginLink],
  );

  if (pathname === AppRoutes.yoloRegister && !needsRedirect && !new URLSearchParams(search).has(accountIdParam)) {
    setNeedsRedirect(true);
  }

  useEffect(() => {
    fetchLoginLink();
    if (view === RegistrationPhase.MFA) {
      const searchCustomerContactPhoneNumbers = async () => {
        const res = await accountResource.searchCustomerContactPhoneNumbers(email, emailJwt ?? undefined);
        setOptions(
          res.map((number: string) => ({
            value: number,
            label: number.substring(0, 2) + ' ' + number.substring(2, 5) + ' ' + number.substring(5),
          })),
        );
      };
      searchCustomerContactPhoneNumbers();
    }
  }, [fetchLoginLink, view, email, emailJwt]);

  useEffect(() => {
    if (!needsRedirect && prevViewRef.current !== view) {
      prevViewRef.current = view;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- needsRedirect is intentionally excluded to prevent duplicate analytics events
  }, [view]);

  const getLoginUrl = async () => {
    return await accountResource.getRegisterLoginUrl();
  };

  const forgottenPasswordLink = useMemo(() => {
    const baseLink = AppRoutes.forgottenPassword.withEmail(encodeURIComponent(email));
    const link = makeLink(baseLink, linkParams);
    if (link) {
      return link;
    } else {
      return baseLink;
    }
  }, [email, linkParams]);

  useEffect(() => {
    if (passwordErrors) {
      reportPasswordErrors(() => {});
    }
  }, [passwordErrors, reportPasswordErrors]);

  const reportRegisterError = useCallback(() => {}, []);
  const emailErrorList = useMemo(
    () =>
      messageWithAction(
        translation.loginRegister.register.box.errorInvalidEmail,
        () => emailError,
        reportRegisterError,
      ),
    [emailError, reportRegisterError, translation.loginRegister.register.box.errorInvalidEmail, messageWithAction],
  );
  const emailWarningList = useMemo(
    () =>
      messageWithAction(
        translation.loginRegister.register.box.errorAccountAlreadyExists(loginLink, forgottenPasswordLink),
        () => emailAlreadyUsed,
        reportRegisterError,
        (s: string) => <span dangerouslySetInnerHTML={{ __html: s }} />,
      ),
    [
      emailAlreadyUsed,
      loginLink,
      forgottenPasswordLink,
      reportRegisterError,
      translation.loginRegister.register.box,
      messageWithAction,
    ],
  );

  return (
    <>
      {needsRedirect && <></>}
      {!needsRedirect && (
        <div className="register-page">
          <div className="register-page__container">
            {view === RegistrationPhase.AcceptTerms && (
              <div className="register-page__container__box">
                <h3>{translation.loginRegister.registerB2bWebView.acceptTerms.title}</h3>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    fontWeight: 'lighter',
                  }}
                >
                  <BuildingIcon
                    style={{
                      width: '24px',
                      height: '24px',
                      marginBottom: '16px',
                    }}
                  />

                  <div>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: translation.loginRegister.registerB2bWebView.acceptTerms.topText(
                          getConfig().termsAndConditionsUrl,
                        ),
                      }}
                    />
                    <p
                      dangerouslySetInnerHTML={{
                        __html: translation.loginRegister.registerB2bWebView.acceptTerms.topList,
                      }}
                    />
                  </div>

                  <ScaleIcon
                    style={{
                      width: '24px',
                      height: '24px',
                      marginBottom: '16px',
                    }}
                  />
                  <p
                    dangerouslySetInnerHTML={{
                      __html: translation.loginRegister.registerB2bWebView.acceptTerms.bottomText,
                    }}
                  />
                  <p
                    dangerouslySetInnerHTML={{
                      __html: translation.loginRegister.registerB2bWebView.acceptTerms.bottomList,
                    }}
                  />
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Button
                      variant={'contained'}
                      disabled={isLoading}
                      onClick={async () => setView(RegistrationPhase.Form)}
                    >
                      {translation.loginRegister.registerB2bWebView.acceptTerms.ok}
                    </Button>
                    <Button
                      variant={'text'}
                      disabled={isLoading}
                      onClick={async () => navigate(AppRoutes.homepage + location.search)}
                    >
                      {translation.loginRegister.registerB2bWebView.acceptTerms.cancel}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {view === RegistrationPhase.Form && (
              <div
                className="register-page__container__box"
                style={{
                  width: '311px',
                  padding: '0px',
                }}
              >
                <h3>{translation.loginRegister.registerB2bWebView.form.title}</h3>
                <div
                  style={{
                    fontSize: 'medium',
                    fontWeight: 'lighter',
                  }}
                >
                  {translation.loginRegister.registerB2bWebView.form.header}
                </div>
                <div className="register-page__container__box__info-box">
                  <UnionIcon style={{ flexShrink: 0, width: '24px', height: '24px' }} />

                  <div
                    style={{
                      lineHeight: '24px',
                    }}
                  >
                    {translation.loginRegister.registerB2bWebView.form.infoBox}
                  </div>
                </div>
                <div className={'register-page__container__box__inputs'}>
                  <Input
                    label={translation.loginRegister.registerB2bWebView.form.email}
                    type={'email'}
                    name={'email'}
                    inputMode={'email'}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    errorList={emailErrorList}
                    warningList={emailWarningList}
                  />
                </div>

                {registerError && <div className="register-page__container__error">{registerError}</div>}

                <div className="register-page__container__action-container register-page__container__action-container--webview">
                  <Button
                    variant={'contained'}
                    disabled={isLoading || !registerButtonEnabled}
                    onClick={async () => await finalizeRegistrationB2b()}
                  >
                    {translation.loginRegister.registerB2bWebView.form.send}
                  </Button>
                </div>
              </div>
            )}
            {view === RegistrationPhase.Result && (
              <div className="register-page__container__box">
                <EnvelopeIcon
                  style={{
                    width: '48px',
                    height: '48px',
                  }}
                />
                <h3>{translation.loginRegister.registerB2bWebView.result.title}</h3>
                {translation.loginRegister.registerB2bWebView.result.header}
                <div
                  style={{
                    display: 'flex',
                  }}
                >
                  <Button
                    variant={'text'}
                    disabled={isLoading}
                    onClick={async () => setView(RegistrationPhase.Form)}
                    style={{ paddingLeft: '0', paddingTop: '0' }}
                  >
                    {translation.loginRegister.registerB2bWebView.result.resend}
                  </Button>
                </div>
                <Button
                  variant={'contained'}
                  disabled={isLoading}
                  onClick={async () => setView(RegistrationPhase.EmailOTP)}
                >
                  {translation.loginRegister.registerB2bWebView.result.send}
                </Button>
              </div>
            )}
            {view === RegistrationPhase.EmailOTP && (
              <div className="register-page__container__box">
                <RegisterYoloOtpWebView
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  onSendAgain={async () => {
                    await yoloResend(registerAccountId);
                  }}
                  onCancelView={() => setView(RegistrationPhase.Form)}
                  onComplete={() => {
                    setView(RegistrationPhase.NewPassword);
                  }}
                  isWebView={true}
                  callback={async otp => {
                    setOtp(otp);
                    await validateOtp(otp);
                  }}
                  header={''}
                  title={translation.loginRegister.registerB2bWebView.emailOtp.title(email)}
                />
              </div>
            )}
            {view === RegistrationPhase.NewPassword && (
              <div className="register-page__container__box">
                <h3>{translation.loginRegister.registerB2bWebView.newPassword.title}</h3>
                <p
                  dangerouslySetInnerHTML={{
                    __html: translation.loginRegister.registerB2bWebView.newPassword.header(email),
                  }}
                ></p>
                <SetPassword
                  password={password}
                  passwordAgain={passwordAgain}
                  passwordHints={getPasswordHints(password, passwordAgain)}
                  hasPasswordError={hasPasswordError}
                  passwordsAreNotMatching={passwordsAreNotMatching}
                  setPassword={setPassword}
                  setPasswordAgain={setPasswordAgain}
                  isLoading={isLoading}
                  onEnter={async () => await completeRegistrationB2b(otp)}
                />
                <Button
                  variant={'contained'}
                  disabled={isLoading}
                  onClick={async () => {
                    await completeRegistrationB2b(otp);
                  }}
                >
                  {translation.loginRegister.registerB2bWebView.newPassword.send}
                </Button>
              </div>
            )}
            {view === RegistrationPhase.MFA && (
              <div className="register-page__container__box">
                <h3>{translation.loginRegister.registerB2bWebView.mfa.title}</h3>
                {translation.loginRegister.registerB2bWebView.mfa.header}
                <div>
                  <Select
                    options={options || []}
                    label={<>{translation.loginRegister.registerB2bWebView.mfa.select}</>}
                    aria-label={translation.loginRegister.registerB2bWebView.mfa.select}
                    name="phone-number"
                    onChange={e => {
                      setPhoneNumber(e);
                    }}
                    value={phoneNumber}
                    isLoading={false}
                    onKeyDown={handleEdit}
                    placeholder={translation.loginRegister.registerB2bWebView.mfa.placeholder}
                  />
                </div>
                <Button
                  variant={'contained'}
                  disabled={isLoading || phoneNumber === null}
                  onClick={async () => {
                    const redirectUrl = await mfaResource.getSmsConnectUrl({
                      phoneNumber: phoneNumber?.value ?? '',
                      redirectType: RedirectType.B2bRegister,
                    });
                    try {
                      const url = new URL(redirectUrl);
                      url.searchParams.set('almfr', 'regb2b');
                      window.location.href = url.toString();
                    } catch (e) {
                      window.location.href = redirectUrl;
                    }
                  }}
                >
                  {translation.loginRegister.registerB2bWebView.mfa.send}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
